import { Injectable } from '@angular/core';

import { EcmDispatcherModule } from '../ecm-dispatcher.module';

/**
 * Local storage access based on `globalThis`
 * Every access is guarded : storage can be unavailable (private browsing, blocked cookies, non browser platform)
 */
@Injectable({
    providedIn: 'root',
})
export class OrangeLocalStorageService {
    private static readonly CLASS_NAME = 'OrangeLocalStorageService';

    /**
     * Get raw value for a key
     * @param {string} key Local storage key
     * @returns {string} Stored value, `null` if absent or storage unavailable
     */
    public get(key: string): string | null {
        try {
            return globalThis.localStorage?.getItem(key) ?? null;
        } catch {
            // storage unavailable
            return null;
        }
    }

    /**
     * Get parsed JSON value for a key
     * @param {string} key Local storage key
     * @returns {T} Parsed value, `null` if absent, unparsable or storage unavailable
     */
    public getJson<T>(key: string): T | null {
        const rawValue = this.get(key);
        if (!rawValue) {
            return null;
        }

        try {
            return JSON.parse(rawValue) as T;
        } catch {
            // malformed JSON
            EcmDispatcherModule.sendConsoleError(
                OrangeLocalStorageService.CLASS_NAME,
                `Malformed JSON for key ${key} in local storage`,
            );
            return null;
        }
    }

    /**
     * Set value for a key
     * @param {string} key Local storage key
     * @param {string} value Value to store
     */
    public set(key: string, value: string): void {
        try {
            globalThis.localStorage?.setItem(key, value);
        } catch {
            // storage unavailable or quota exceeded
        }
    }

    /**
     * Remove a key
     * @param {string} key Local storage key
     */
    public remove(key: string): void {
        try {
            globalThis.localStorage?.removeItem(key);
        } catch {
            // storage unavailable
        }
    }
}
